import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { getPaymentStatus } from "../services/payments";
import { markCasePaid } from "../services/cases";
import { Button } from "../components/ui/button";
import { fmtUSD } from "../lib/pricing";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null });
  const done = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", data: null }); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          if (!done.current) {
            done.current = true;
            try { await markCasePaid(res.case_id, { paymentTransactionId: sessionId, paidAmount: res.amount }); } catch { /* owner-only; ignore */ }
          }
          setState({ status: "paid", data: res });
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLLS) { setState({ status: "timeout", data: res }); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        attempts += 1;
        if (attempts >= MAX_POLLS) { setState({ status: "error", data: null }); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  const goCase = () => navigate(state.data?.case_id ? `/cases/${state.data.case_id}` : "/lab");

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-20 text-center" data-testid="payment-success-page">
      {state.status === "polling" && (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-brand" />
          <h1 className="mt-4 font-heading text-2xl font-bold">{t("payment.confirming")}</h1>
          <p className="mt-2 text-muted-foreground">{t("payment.confirmingHint")}</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-emerald-500" data-testid="payment-success-icon" />
          <h1 className="mt-4 font-heading text-2xl font-bold">{t("payment.successTitle")}</h1>
          <p className="mt-2 text-muted-foreground">{t("payment.successBody")}</p>
          {state.data?.amount != null && <p className="mt-1 font-heading text-xl font-bold">{fmtUSD(state.data.amount)}</p>}
          <Button className="mt-6 rounded-full bg-brand text-white hover:bg-brand/90" onClick={goCase} data-testid="payment-success-continue">
            {t("payment.backToCase")}
          </Button>
        </>
      )}
      {(state.status === "timeout" || state.status === "error") && (
        <>
          <AlertCircle className="h-12 w-12 text-amber-500" />
          <h1 className="mt-4 font-heading text-2xl font-bold">{t("payment.pendingTitle")}</h1>
          <p className="mt-2 text-muted-foreground">{t("payment.pendingBody")}</p>
          <Button className="mt-6 rounded-full" variant="outline" onClick={goCase} data-testid="payment-pending-continue">
            {t("payment.backToCase")}
          </Button>
        </>
      )}
    </div>
  );
}
